import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';


const VenueForm = ({ formData, onFormChange, onNext, onPrevious }) => {
  const [venueType, setVenueType] = useState(formData?.venueType || 'physical');

  const venueTypeOptions = [
    { value: 'physical', label: 'Physical Location' },
    { value: 'virtual', label: 'Virtual Event' },
    { value: 'hybrid', label: 'Hybrid (Physical + Virtual)' }
  ];

  const countryOptions = [
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'ca', label: 'Canada' },
    { value: 'au', label: 'Australia' },
    { value: 'de', label: 'Germany' },
    { value: 'fr', label: 'France' },
    { value: 'in', label: 'India' },
    { value: 'other', label: 'Other' }
  ];

  const accessibilityFeatures = [
    { id: 'wheelchair', label: 'Wheelchair Accessible' },
    { id: 'parking', label: 'Accessible Parking' },
    { id: 'restrooms', label: 'Accessible Restrooms' },
    { id: 'hearing', label: 'Hearing Loop/Assistive Listening' },
    { id: 'visual', label: 'Visual Aids Available' },
    { id: 'service_animals', label: 'Service Animals Welcome' }
  ];

  const handleVenueTypeChange = (value) => {
    setVenueType(value);
    onFormChange({ venueType: value });
  };

  const handleAccessibilityChange = (featureId, checked) => {
    const currentFeatures = formData?.accessibilityFeatures || [];
    const updatedFeatures = checked
      ? [...currentFeatures, featureId]
      : currentFeatures?.filter(id => id !== featureId);
    
    onFormChange({ accessibilityFeatures: updatedFeatures });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onNext();
  };

  return (
    <div className="bg-white border border-border rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Venue Information</h2>
        <p className="text-muted-foreground">Configure location and accessibility details</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <Select
          label="Venue Type"
          placeholder="Select venue type"
          options={venueTypeOptions}
          value={venueType}
          onChange={handleVenueTypeChange}
          required
          description="Choose how attendees will participate"
        />

        {(venueType === 'physical' || venueType === 'hybrid') && (
          <div className="space-y-6 p-4 bg-muted rounded-lg">
            <h3 className="text-lg font-medium text-foreground">Physical Location Details</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Input
                  label="Venue Name"
                  type="text"
                  placeholder="Enter venue name"
                  value={formData?.venueName || ''}
                  onChange={(e) => onFormChange({ venueName: e?.target?.value })}
                  required
                />

                <Input
                  label="Street Address"
                  type="text"
                  placeholder="Enter street address"
                  value={formData?.address || ''}
                  onChange={(e) => onFormChange({ address: e?.target?.value })}
                  required
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="City"
                    type="text"
                    placeholder="Enter city"
                    value={formData?.city || ''}
                    onChange={(e) => onFormChange({ city: e?.target?.value })}
                    required
                  />
                  <Input
                    label="State/Province"
                    type="text"
                    placeholder="Enter state"
                    value={formData?.state || ''}
                    onChange={(e) => onFormChange({ state: e?.target?.value })}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input
                    label="ZIP/Postal Code"
                    type="text"
                    placeholder="Enter ZIP code"
                    value={formData?.zipCode || ''}
                    onChange={(e) => onFormChange({ zipCode: e?.target?.value })}
                    required
                  />
                  <Select
                    label="Country"
                    placeholder="Select country"
                    options={countryOptions}
                    value={formData?.country || ''}
                    onChange={(value) => onFormChange({ country: value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Location Preview
                  </label>
                  <div className="w-full h-64 bg-muted rounded-lg border border-border overflow-hidden">
                    <iframe
                      width="100%"
                      height="100%"
                      loading="lazy"
                      title="Venue Location"
                      referrerPolicy="no-referrer-when-downgrade"
                      src="https://www.google.com/maps?q=37.7749,-122.4194&z=14&output=embed"
                      className="border-0"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Map will update based on the address provided
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Venue Capacity"
                type="number"
                placeholder="Enter maximum capacity"
                value={formData?.capacity || ''}
                onChange={(e) => onFormChange({ capacity: e?.target?.value })}
                required
                min="1"
                description="Maximum number of attendees"
              />
              <Input
                label="Parking Information"
                type="text"
                placeholder="e.g., Free parking available"
                value={formData?.parking || ''}
                onChange={(e) => onFormChange({ parking: e?.target?.value })}
                description="Optional parking details"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                Accessibility Features
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {accessibilityFeatures?.map((feature) => (
                  <Checkbox
                    key={feature?.id}
                    label={feature?.label}
                    checked={(formData?.accessibilityFeatures || [])?.includes(feature?.id)}
                    onChange={(e) => handleAccessibilityChange(feature?.id, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {(venueType === 'virtual' || venueType === 'hybrid') && (
          <div className="space-y-6 p-4 bg-accent rounded-lg">
            <h3 className="text-lg font-medium text-foreground">Virtual Event Details</h3>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Input
                label="Platform"
                type="text"
                placeholder="e.g., Zoom, Teams, Custom Platform"
                value={formData?.platform || ''}
                onChange={(e) => onFormChange({ platform: e?.target?.value })}
                required
                description="Virtual meeting platform"
              />
              <Input
                label="Meeting Link"
                type="url"
                placeholder="https://..."
                value={formData?.meetingLink || ''}
                onChange={(e) => onFormChange({ meetingLink: e?.target?.value })}
                description="Will be shared with attendees"
              />
            </div>

            <Input
              label="Virtual Capacity"
              type="number"
              placeholder="Enter maximum virtual attendees"
              value={formData?.virtualCapacity || ''}
              onChange={(e) => onFormChange({ virtualCapacity: e?.target?.value })}
              required
              min="1"
              description="Maximum number of virtual participants"
            />
          </div>
        )}

        <div className="flex justify-between pt-6 border-t border-border">
          <Button type="button" variant="outline" onClick={onPrevious} iconName="ArrowLeft">
            Back to Details
          </Button>
          <Button type="submit" iconName="ArrowRight" iconPosition="right">
            Continue to Ticketing
          </Button>
        </div>
      </form>
    </div>
  );
};

export default VenueForm;